<template>
    <navigateable-tab>
        <template>
            <div>
                <h3 class="category-title">{{resource('AudienceManager_Builder_Quickstart_Title')}}</h3>

                <div class="question-container">
                    <label class="question-name" for="audience-csv-upload">
                        {{resource('AudienceManager_Builder_CSVUpload_Label')}} *
                    </label>
                    
                    <div class="question-content">
                        <button class="btn btn-outline-secondary question-input" @click="$refs.audienceUpload.click()" :disabled="!isNewAudience">
                            {{resource('AudienceManager_Builder_SelectCsv_Button')}}

                            <!-- Hidden file input to handle CSV upload -->
                            <input id="audience-csv-upload"
                                   type="file"
                                   accept="text/csv"

                                   v-show="false"
                                   @change="audienceSelected"
                                   ref="audienceUpload"
                            />
                        </button>
                        <div v-if="!this.$v.csvFile.$invalid">
                            <span>
                                {{this.csvFile.name}}
                            </span>
                        </div>
                        <template v-if="displayCsvFileValidationMessage">
                            <error-message class="validation-message" v-if="!$v.csvFile.mustBeCsv" message="must be a valid csv file." field-name="csvFile" />
                            <required-error-message class="validation-message" v-if="!$v.csvFile.required" field-name="csvFile" />
                        </template>
                    </div>
                </div>
              
                <div class="question-container">
                    <label class="question-name" for="audience-name">
                        {{resource('AudienceEditModel_Name_DisplayName')}} *
                    </label>

                    <div class="question-content">
                        <!-- Input validation looks fairly different when compared to DNDS, skip for the time being -->
                        <Input  id="audience-name"
                                class="question-input"
                                v-model="audienceNameModel"  
                                :disabled="!canEditAudience" 
                        />
                        
                        <template v-if="displayAudienceNameValidationMessage">
                            <required-error-message v-if="!$v.audienceName.required" :field-name="resource('AudienceEditModel_Name_DisplayName')" />
                            <max-length-error-message v-if="!$v.audienceName.maxLength" :field-name="resource('AudienceEditModel_Name_DisplayName')" :max-length="255" />
                        </template>
                    </div>
                </div>
            </div>
        </template>
    </navigateable-tab>
</template>

<script>
    import {RouteNames} from "@modules/AudienceManager/constants";

    import {createNamespacedHelpers} from 'vuex';
    import resourceMixin from "@core/Util/Mixins/resources";

    import {validationMixin} from 'vuelidate';
    import {maxLength, required} from "vuelidate/lib/validators";
    import {bootstrapInfoGrowl, bootstrapDangerGrowl} from "@core/Util/growler";
    import loader from "@core/Util/loader";

    import NavigateableTab from "./../NavigateableTab";
    import RequiredErrorMessage from "@core/Components/Errors/RequiredErrorMessage";
    import ErrorMessage from "@core/Components/Errors/ErrorMessage";
    import MaxLengthErrorMessage from "@core/Components/Errors/MaxLengthErrorMessage";
    
    const mustBeCsv = value => {
        if(!value) return true;
        return value.name.endsWith('.csv');
    }
    const {mapState, mapGetters, mapMutations, mapActions} = createNamespacedHelpers('audienceManager/builder');
    
    export default {
        name: "QuickstartTab",
        mixins: [resourceMixin, validationMixin],
        components: {
            NavigateableTab,
            RequiredErrorMessage,
            MaxLengthErrorMessage,
            ErrorMessage
        },
        validations: {
            audienceName: {
                required,
                maxLength: maxLength(255)
            },
            csvFile: {
                required,
                mustBeCsv
            }
        },
        async beforeRouteLeave (to, from, next) {

            //Audience name and valid csv file are required.
            if(!this.$v.audienceName.$invalid && !this.$v.csvFile.$invalid) {
                
                if(this.isNewAudience) {
                    try {
                        loader.show();
                        await this.createAudienceFromCsv(this.csvFile);
                        bootstrapInfoGrowl(this.resource('AudienceManager_Builder_AudienceUploaded_Message'));
                    } catch(e){
                        bootstrapDangerGrowl(e);
                    } finally {
                        loader.hide();
                    }
                }
                
                next();
                return;
            }
            
            //We should not prevent users from navigating away from the builder
            const isBuilderRoute = to.matched.some(r => r.name === RouteNames.BUILDER);
            
            if(!isBuilderRoute){
                next();
                return;
            }
            
            //Display errors
            this.$v.csvFile.$touch();
            this.$v.audienceName.$touch();
            next(false);
        },
        methods: {
            async audienceSelected(evt){
                this.csvFile = evt.target.files[0];

                if(this.$v.csvFile.$invalid) {
                    this.$v.csvFile.$touch();
                }
            },
            ...mapMutations(['setAudienceName']),
            ...mapActions(['createAudienceFromCsv'])
        },
        computed: {
            ...mapState(['audienceName', 'isNewAudience']),
            ...mapGetters(['canEditAudience']),
            audienceNameModel: {
                get(){
                    return this.audienceName;
                },
                set(value){
                    this.setAudienceName(value);
                    this.$v.audienceName.$touch();
                }
            },
            displayAudienceNameValidationMessage() {
                return this.$v.audienceName.$invalid && this.$v.audienceName.$dirty;
            },
            displayCsvFileValidationMessage() {
                return this.$v.csvFile.$invalid && this.$v.csvFile.$dirty;
            }
        },
        data() {
            return {
                csvFile: undefined
            }
        }
    }
</script>

<style scoped lang="scss" src="./quickstart-tab.scss" />